import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { SEO } from '../components/SEO';
import { ArrowLeftIcon, ArrowRightIcon, ChevronRightIcon, InfoIcon, TargetIcon, ExternalLinkIcon } from 'lucide-react';
import { clientCases, getClientCaseBySlug } from '../data/clientCases';
export const ClientCasePage: React.FC = () => {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const clientCase = getClientCaseBySlug(slug || '');
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!clientCase) {
    return <Navigate to="/" replace data-id="element-973" />;
  }
  const relatedCases = clientCases.filter(c => c.slug !== clientCase.slug).slice(0, 3);
  const renderContentBlock = (block: any, index: number) => {
    switch (block.type) {
      case 'heading':
        return <h2 key={index} className="text-2xl md:text-3xl font-bold text-[#111827] mt-12 mb-6" data-id="element-974">
            {block.content}
          </h2>;
      case 'heading3':
        return <h3 key={index} className="text-xl md:text-2xl font-bold text-[#111827] mt-8 mb-4" data-id="element-975">
            {block.content}
          </h3>;
      case 'paragraph':
        return <p key={index} className="text-lg text-[#4B5563] leading-relaxed mb-6" data-id="element-976">
            {block.content}
          </p>;
      case 'list':
        return <ul key={index} className="space-y-3 mb-8 ml-6 list-disc text-lg text-[#4B5563]" data-id="element-977">
            {block.items?.map((item: string, i: number) => <li key={i} className="pl-2 leading-relaxed" data-id="element-978">
                {item}
              </li>)}
          </ul>;
      case 'callout':
        return <div key={index} className="bg-[#F7F8FA] border-l-4 border-[#007AFF] p-6 rounded-r-lg my-8" data-id="element-979">
            <div className="flex items-start gap-4" data-id="element-980">
              <InfoIcon className="w-6 h-6 text-[#007AFF] flex-shrink-0 mt-1" data-id="element-981" />
              <p className="text-lg text-[#1B2B44] font-medium leading-relaxed m-0" data-id="element-982">
                {block.content}
              </p>
            </div>
          </div>;
      case 'formula':
        return <div key={index} className="bg-[#1B2B44] text-white p-8 rounded-xl my-8 text-center shadow-lg" data-id="element-983">
            <span className="text-sm text-white/60 uppercase tracking-wider font-semibold mb-3 block" data-id="element-984">
              De Formule
            </span>
            <p className="text-xl md:text-2xl font-bold leading-relaxed" data-id="element-985">
              {block.content}
            </p>
          </div>;
      default:
        return null;
    }
  };
  return <div className="w-full min-h-screen bg-white font-sans" data-id="element-986">
      <SEO title={`${clientCase.clientName} | Klantcase Noord Digital`} description={clientCase.metaDescription} canonical={`https://noorddigital.nl/cases/${clientCase.slug}`} data-id="element-987" />
      <Header data-id="element-988" />

      {/* Breadcrumb & Hero */}
      <div className="bg-[#F7F8FA] pt-24 pb-16 border-b border-gray-200" data-id="element-989">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-990">
          <nav className="flex items-center text-sm text-[#6B7280] mb-8" data-id="element-991">
            <Link to="/" className="hover:text-[#007AFF] transition-colors" data-id="element-992">
              Home
            </Link>
            <ChevronRightIcon className="w-4 h-4 mx-2" data-id="element-993" />
            <Link to="/bibliotheek" className="hover:text-[#007AFF] transition-colors" data-id="element-994">
              Bibliotheek
            </Link>
            <ChevronRightIcon className="w-4 h-4 mx-2" data-id="element-995" />
            <span className="text-[#111827] font-medium truncate" data-id="element-996">
              {clientCase.clientName}
            </span>
          </nav>

          <div className="max-w-3xl" data-id="element-997">
            <span className="inline-block px-3 py-1 bg-blue-100 text-[#007AFF] text-sm font-semibold rounded-full mb-6" data-id="element-998">
              {clientCase.sector}
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-[#111827] leading-tight mb-6" data-id="element-999">
              {clientCase.clientName}
            </h1>
            <p className="text-xl text-[#6B7280] leading-relaxed" data-id="element-1000">
              {clientCase.shortDescription}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content & Sidebar */}
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px] py-16" data-id="element-1001">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12" data-id="element-1002">
          {/* Article Content */}
          <div className="lg:col-span-8" data-id="element-1003">
            <div className="mb-12 rounded-2xl overflow-hidden shadow-lg" data-id="element-1004">
              <img src={clientCase.heroImage} alt={`${clientCase.clientName} case study`} className="w-full h-auto object-cover max-h-[500px]" style={{
              objectPosition: clientCase.objectPosition || 'center top'
            }} data-id="element-1005" />
            </div>
            <article className="prose prose-lg max-w-none prose-headings:text-[#111827] prose-p:text-[#4B5563] prose-a:text-[#007AFF]" data-id="element-1006">
              {clientCase.content.map((block, index) => renderContentBlock(block, index))}
            </article>

            {/* Author / Bio block */}
            <div className="mt-16 pt-8 border-t border-gray-200 flex items-center gap-6" data-id="element-1007">
              <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden" className="w-16 h-16 rounded-full bg-[#F7F8FA] border border-gray-200" data-id="element-1008" />
              <div data-id="element-1009">
                <h4 className="font-bold text-[#111827]" data-id="element-1010">Norden</h4>
                <p className="text-[#6B7280] text-sm" data-id="element-1011">
                  Klantcases bij Noord Digital
                </p>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4" data-id="element-1012">
            <div className="sticky top-24 space-y-8" data-id="element-1013">
              {/* Back to Bibliotheek */}
              <Link to="/bibliotheek" className="flex items-center justify-center gap-2 w-full py-4 px-6 bg-[#F7F8FA] hover:bg-gray-100 text-[#1B2B44] font-semibold rounded-xl transition-colors border border-gray-200" data-id="element-1014">
                <ArrowLeftIcon className="w-5 h-5" data-id="element-1015" />
                Terug naar Bibliotheek
              </Link>

              {/* Bezoek de live website */}
              {clientCase.website && <a href={clientCase.website} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full py-4 px-6 bg-white hover:bg-[#007AFF] text-[#007AFF] hover:text-white font-semibold rounded-xl transition-colors border-2 border-[#007AFF]" data-id="element-1016">
                  <ExternalLinkIcon className="w-5 h-5" data-id="element-1017" />
                  Bekijk de website
                </a>}

              {/* CTA Card */}
              <div className="bg-gradient-to-br from-[#007AFF] to-[#1B2B44] rounded-2xl p-8 text-white shadow-xl" data-id="element-1018">
                <h3 className="text-xl font-bold mb-3" data-id="element-1019">
                  Ook zulke resultaten behalen?
                </h3>
                <p className="text-white/80 text-sm leading-relaxed mb-6" data-id="element-1020">
                  Ontdek wat we voor jouw bedrijf kunnen betekenen. Vraag een
                  gratis scan aan en we laten je direct zien waar je kansen
                  liggen.
                </p>
                <a href="/prijsscan" className="block w-full py-3 px-4 bg-white text-[#007AFF] text-center font-bold rounded-lg hover:bg-gray-50 transition-colors" data-id="element-1021">
                  Start de SEO scan
                </a>
              </div>

              {/* Related Cases */}
              {relatedCases.length > 0 && <div className="bg-white border border-gray-200 rounded-2xl p-6" data-id="element-1022">
                  <h3 className="text-lg font-bold text-[#111827] mb-4 flex items-center gap-2" data-id="element-1023">
                    <TargetIcon className="w-5 h-5 text-[#007AFF]" data-id="element-1024" />
                    Andere cases
                  </h3>
                  <ul className="space-y-3" data-id="element-1025">
                    {relatedCases.map((relCase, idx) => <li key={idx} data-id="element-1026">
                        <Link to={`/cases/${relCase.slug}`} className="text-sm text-[#4B5563] hover:text-[#007AFF] hover:underline flex items-start gap-2 leading-snug" data-id="element-1027">
                          <ChevronRightIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" data-id="element-1028" />
                          {relCase.clientName}
                        </Link>
                      </li>)}
                  </ul>
                </div>}
            </div>
          </div>
        </div>
      </div>

      {/* More Cases Grid */}
      {relatedCases.length > 0 && <div className="bg-[#F7F8FA] py-20 border-t border-gray-200" data-id="element-1029">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1030">
            <h2 className="text-2xl md:text-3xl font-bold text-[#111827] mb-10 text-center" data-id="element-1031">
              Meer succesverhalen
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1032">
              {relatedCases.map((relCase, idx) => <Link key={idx} to={`/cases/${relCase.slug}`} className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg hover:border-[#007AFF]/30 transition-all group" data-id="element-1033">
                  <div className="h-40 mb-4 overflow-hidden rounded-lg" data-id="element-1034">
                    <img src={relCase.heroImage} alt={relCase.clientName} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" style={{
                objectPosition: relCase.objectPosition || 'center top'
              }} data-id="element-1035" />
                  </div>
                  <h3 className="text-lg font-bold text-[#1B2B44] mb-3 group-hover:text-[#007AFF] transition-colors" data-id="element-1036">
                    {relCase.clientName}
                  </h3>
                  <p className="text-[#6B7280] text-sm leading-relaxed mb-4 line-clamp-3" data-id="element-1037">
                    {relCase.shortDescription}
                  </p>
                  <span className="text-[#007AFF] text-sm font-semibold flex items-center gap-1" data-id="element-1038">
                    Lees de case <ArrowRightIcon className="w-4 h-4" data-id="element-1039" />
                  </span>
                </Link>)}
            </div>
          </div>
        </div>}

      <Footer data-id="element-1040" />
    </div>;
};